"use client"

import { DotsHorizontalIcon, TrashIcon } from "@radix-ui/react-icons"
import { Row } from "@tanstack/react-table"

import { Button } from "@/app/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu"
import axios from "axios"
import { Consignor } from "@prisma/client"
import { Flex } from "@radix-ui/themes"
import { useQueryClient } from "@tanstack/react-query"
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogCancel, AlertDialogHeader, AlertDialogDescription } from "../components/ui/alert-dialog"
import { useState } from "react"
import { Loader2, PencilIcon } from "lucide-react"
import { constructFullName } from "@/lib/utils"
import { useRouter } from "next/navigation"
import { mutate } from "swr"
interface DataTableRowActionsProps<TData> {
    row: Row<TData>
}

export function DataTableRowActions<TData>({
    row,
}: DataTableRowActionsProps<TData>) {
    const queryClient = useQueryClient();
    const { original: { cocode, name, lastName } } =
        row as { original: Consignor };

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const router = useRouter();

    const deleteConsignor = async () => {
        try {
            setLoading(true);
            await axios.delete(`/api/v1/consignors/${cocode}`);
            mutate("/api/v1/consignors");
            setOpen(false);
        } catch (error) {
            console.log("Unable to delete consignor!");
        } finally {
            setLoading(false);
        }
    }

    return (
        <Flex justify="end">
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-semibold text-lg">Confirm Delete</AlertDialogTitle>
                        <AlertDialogDescription className="text-black">
                            Are you sure you want to delete this consignor profile {name || lastName ? `for ${constructFullName(name, lastName)}` : ''} (Consignor Code: {cocode})?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <Flex justify="end" align="center" className="space-x-3">
                        <AlertDialogCancel>Cancel</AlertDialogCancel>

                        <Button
                            disabled={loading}
                            variant="destructive"
                            onClick={async () => { await deleteConsignor() }}
                        >
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Delete
                        </Button>
                    </Flex>
                </AlertDialogContent>
            </AlertDialog>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
                    >
                        <DotsHorizontalIcon className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-content">
                    <DropdownMenuItem className="text-sm" onClick={() => {
                        router.push(`/consignors/${cocode}`);
                    }}>
                        <PencilIcon className="mr-2 h-4 w-4" />
                        Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-sm text-red-500 focus:text-red-600" onClick={() => setOpen(true)}>
                        <TrashIcon className="mr-2 h-4 w-4" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu >
        </Flex>
    )
} 
